import { Component, Inject, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { SelectPesquisavelComponent } from '../../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AlertCardComponent } from '../../../../../../shared/components/alert-card/alert-card.component';
import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { CategoriaFinanceiraService } from '../../../../../../shared/services/apis/financas/categoria-financeira.service';
import { CategoriaFinanceira } from '../../../../../../shared/services/models/financas/categoria-financeira.model';
import { MoverCategoriaFinanceiraRequest } from '../../../../../../shared/services/requests/financas/mover-categoria-financeira.request';
import { MigracaoCategoriaModalComponent } from '../../../config-financas-shared/migracao-categoria-modal/migracao-categoria-modal.component';

export interface MoverCategoriasDialogData {
  idsCategorias: number[];
  nomeCategorias: string[];
  /** Maior profundidade de subárvore entre os itens selecionados (0 = sem filhos, 1 = tem filhos, 2 = tem netos). */
  maiorProfundidadeSubarvore: number;
  /** Tipos (Receita/Despesa) distintos entre os itens selecionados. */
  tiposSelecionados: number[];
}

@Component({
  selector: 'app-dialog-mover-categorias-financeiras',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SelectPesquisavelComponent,
    AlertCardComponent,
  ],
  templateUrl: './dialog-mover-categorias-financeiras.component.html',
  styleUrls: ['./dialog-mover-categorias-financeiras.component.css'],
})
export class DialogMoverCategoriasFinanceirasComponent implements OnInit {
  public form: FormGroup;
  public isLoading: boolean = false;
  public isLoadingLookups: boolean = false;
  public categoriasPai: CategoriaFinanceira[] = [];
  public categoriasPaiValidas: CategoriaFinanceira[] = [];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<DialogMoverCategoriasFinanceirasComponent>,
    private categoriaService: CategoriaFinanceiraService,
    private alertService: AlertService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: MoverCategoriasDialogData
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      idCategoriaPai: [null],
    });
    this.carregarLookups();
  }

  private carregarLookups(): void {
    this.isLoadingLookups = true;
    this.categoriaService.getLookups().subscribe({
      next: res => {
        this.isLoadingLookups = false;
        if (res.isError) {
          this.alertService.show(
            'Aviso',
            'Não foi possível carregar as categorias disponíveis.',
            AlertType.warning
          );
          return;
        }

        this.categoriasPai = res.categoriasPai || [];
        this.filtrarCategoriasPaiValidas();
      },
      error: () => {
        this.isLoadingLookups = false;
        this.alertService.show('Aviso', 'Falha ao carregar os dados.', AlertType.warning);
      },
    });
  }

  public mover(idCategoriaMigracao?: number): void {
    this.isLoading = true;
    const request = new MoverCategoriaFinanceiraRequest();
    request.idsCategorias = this.data.idsCategorias;
    request.idCategoriaFinanceiraPaiDestino = this.form.value.idCategoriaPai ?? null;
    if (idCategoriaMigracao) {
      request.idCategoriaMigracao = idCategoriaMigracao;
    }

    this.categoriaService.moverEmLote(request).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res.isError) {
          if (res.errorCode === -999 && res.idCategoriaBloqueada) {
            const bloqueado = this.categoriasPai.find(
              c => c.idCategoriaFinanceira === res.idCategoriaBloqueada
            );

            const migracaoDialogRef = this.dialog.open(MigracaoCategoriaModalComponent, {
              width: '500px',
              data: {
                idCategoriaOrigem: res.idCategoriaBloqueada,
                idTipoCategoriaFinanceira: bloqueado?.idTipoCategoriaFinanceira,
                mensagemExibicao: res.errorDescription,
              },
            });

            migracaoDialogRef.afterClosed().subscribe((idDestino: number | undefined) => {
              if (idDestino) {
                this.mover(idDestino);
              }
            });
            return;
          }
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso', 'Categorias movidas com sucesso.', AlertType.success);
        this.dialogRef.close(true);
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha ao mover categorias.', AlertType.warning);
      },
    });
  }

  public cancelar(): void {
    this.dialogRef.close(false);
  }

  /**
   * Filtra as categorias pai candidatas: exclui as que são os próprios itens selecionados,
   * as que estourariam o limite de 3 níveis de hierarquia (considerando a subárvore movida),
   * e as de tipo (Receita/Despesa) diferente dos itens selecionados.
   */
  private filtrarCategoriasPaiValidas(): void {
    const tipos = this.data.tiposSelecionados;
    const tipoUnico = tipos.length === 1 ? tipos[0] : null;

    this.categoriasPaiValidas = this.categoriasPai.filter(cat => {
      if (this.data.idsCategorias.includes(cat.idCategoriaFinanceira)) return false;

      // categoriasPai já vem limitada pelo backend a níveis 1 e 2 (sem netos)
      const nivelCandidato = cat.idCategoriaFinanceiraPai == null ? 1 : 2;
      if (nivelCandidato + 1 + this.data.maiorProfundidadeSubarvore > 3) return false;

      // Seleção com tipos mistos: nenhuma categoria específica serve (só "sem pai" continua válido)
      if (tipoUnico !== null && cat.idTipoCategoriaFinanceira !== tipoUnico) return false;

      return true;
    });
  }
}
